import { Autocomplete, Grid } from "@mui/material";
import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../Components/LayoutContainers/DashboardLayout";
import MDBox from "../../Components/MDBox";
import MDInput from "../../Components/MDInput";
import MDTypography from "../../Components/MDTypography";
import DashboardNavbar from "../../Components/Navbars/DashboardNavbar";
import { ErrorToaast, SuccessToaast } from "../../utils/toast/Toast";
import MDButton from "../../Components/MDButton";
import { useFormik } from "formik";
import * as Yup from "yup";
import { BillModel } from "../../Api/Models/bill";
import { TarefeModel } from "../../Api/Models/Tarefe";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ConvertDateToFa from "../../utils/tools/DateToFa";
import DeleteDialog from "./components/DeleteDialog";

const validationSchema = Yup.object().shape({
    year: Yup.string().typeError("سال الزامی است").required("سال الزامی است "),
    Motaghazi: Yup.string().typeError("متقاضی الزامی است").required("متقاضی الزامی است "),
    NationalCode: Yup.string().typeError("کد ملی الزامی است").required("کد ملی الزامی است "),
});

const YEARS = Array.from({ length: 21 }, (_, i) => (1390 + i).toString());

interface BillRowProps {
    data: any;
    index: number;
    onRowClick: (data: any) => void;
    onEditClick: (data: any) => void;
    onDeleteClick: (id: number) => void;
    onPrintClick: (id: number) => void;
    onChangeStatusClick: (id: number, currentStatus: boolean) => void;
}

const BillRow = React.memo(({ data, index, onRowClick, onEditClick, onDeleteClick, onPrintClick, onChangeStatusClick }: BillRowProps) => {
    return (
        <MDBox
            bgColor={index % 2 === 0 ? "#fbfbfb" : "white"}
            px={1}
            onClick={() => onRowClick(data)}
            sx={{ ":hover": { background: "#9185c8" }, cursor: "pointer" }}
        >
            <Grid container columns={16} textAlign="center" gap="0 10px" wrap="nowrap" alignItems="center">
                <Grid item xs={1}>
                    <MDTypography fontSize={"14px"}>{data.code}</MDTypography>
                </Grid>
                <Grid item xs={1}>
                    <MDTypography fontSize={"14px"}>{data.year}</MDTypography>
                </Grid>
                <Grid item xs={2}>
                    <MDTypography fontSize={"14px"}>{data.motaghazi}</MDTypography>
                </Grid>
                <Grid item xs={2}>
                    <MDTypography fontSize={"14px"}>{data.nationalCode}</MDTypography>
                </Grid>
                <Grid item xs={2}>
                    <MDTypography fontSize={"14px"}>{data.plateNumber}</MDTypography>
                </Grid>
                <Grid item xs={1}>
                    <MDTypography fontSize={"14px"}>{data.taxiCode || '-'}</MDTypography>
                </Grid>
                <Grid item xs={1}>
                    <MDTypography fontSize={"14px"}>{data.totalAmount}</MDTypography>
                </Grid>
                <Grid item xs={1} alignSelf="center">
                    <MDButton
                        size="small"
                        variant={data.isPaid ? "contained" : "outlined"}
                        color={data.isPaid ? "success" : "error"}
                        sx={{
                            fontSize: "11px",
                            px: 1,
                            py: 0.5,
                            minWidth: "auto",
                            whiteSpace: "nowrap",
                            fontWeight: "bold",
                        }}
                        onClick={(e: any) => {
                            e.stopPropagation();
                            onChangeStatusClick(Number(data.id), Boolean(data.isPaid));
                        }}
                    >
                        {data.isPaid ? "پرداخت شده" : "پرداخت نشده"}
                    </MDButton>
                </Grid>
                <Grid item xs={1}>
                    <MDTypography fontSize={"14px"}>{ConvertDateToFa(data.issueDate)}</MDTypography>
                </Grid>
                <Grid item xs={1}>
                    <MDTypography fontSize={"14px"}>{ConvertDateToFa(data.paidDate)}</MDTypography>
                </Grid>
                <Grid item xs={1} alignSelf="center">
                    <MDBox
                        onClick={(e: any) => {
                            e.stopPropagation();
                            onEditClick(data);
                        }}
                        sx={{ cursor: "pointer", display: "inline-block" }}
                    >
                        <EditIcon color="info" />
                    </MDBox>
                </Grid>
                <Grid item xs={1} alignSelf="center">
                    <MDBox
                        onClick={(e: any) => {
                            e.stopPropagation();
                            onDeleteClick(Number(data.id));
                        }}
                        sx={{ cursor: "pointer", display: "inline-block" }}
                    >
                        <DeleteIcon color="error" />
                    </MDBox>
                </Grid>
                <Grid item xs={1} alignSelf="center">
                    <MDBox
                        onClick={(e: any) => {
                            e.stopPropagation();
                            onPrintClick(data.id);
                        }}
                        sx={{ cursor: "pointer", display: "inline-block" }}
                    >
                        <PrintIcon color="warning" />
                    </MDBox>
                </Grid>
            </Grid>
        </MDBox>
    );
});

interface TariffItemRowProps {
    item: any;
    value: any;
    onChange: (id: any, value: any) => void;
    onAdd: (item: any) => void;
}

const TariffItemRow = React.memo(({ item, value, onChange, onAdd }: TariffItemRowProps) => {
    return (
        <Grid container spacing={2} item xs={12} alignItems="center">
            <Grid item xs={2}>
                <MDTypography fontSize="15px" sx={{ fontWeight: 'bold' }}>
                    {item.title}
                </MDTypography>
            </Grid>

            <Grid item xs={2}>
                <MDTypography fontSize="14px">
                    {item.amountType == 1
                        ? 'ثابت'
                        : item.amountType == 2
                            ? 'درصدی'
                            : item.amountType == 3
                                ? 'روزانه'
                                : item.amountType == 4
                                    ? 'ساعتی'
                                    : ''}
                </MDTypography>
            </Grid>

            <Grid item xs={2}>
                <MDTypography fontSize="14px">مقدار</MDTypography>
            </Grid>

            <Grid item xs={3}>
                <MDInput
                    InputProps={{ readOnly: item.amountType == 1 }}
                    fullWidth
                    size="small"
                    value={item.amountType == 1 ? item.fixedAmount : (value || '')}
                    onChange={(e: any) => onChange(item.id, e.target.value)}
                />
            </Grid>

            <Grid item xs={2}>
                <MDButton
                    fullWidth
                    size="small"
                    variant="outlined"
                    color="success"
                    onClick={() => onAdd(item)}
                >
                    اضافه کردن
                </MDButton>
            </Grid>
        </Grid>
    );
});

export default function AvarezKhodro() {
    const billmodel = useMemo(() => new BillModel(), []);
    const tModel = useMemo(() => new TarefeModel(), []);
    const navigate = useNavigate();
    const [bills, setBills] = useState<any[] | null>(null);
    const [counter, setCounter] = useState(0);
    const [tarefe, setTarefe] = useState<any[]>([]);
    const [tariffSearch, setTariffSearch] = useState("");
    const [values, setValues] = useState<{ [key: string | number]: any }>({});
    const [selectedItems, setSelectedItems] = useState<any[]>([]);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [deleteOpen, setDeleteOpen] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });
    const containerRef = useRef<HTMLDivElement | null>(null);

    const filteredTarefe = useMemo(() => {
        if (!tariffSearch || !tariffSearch.trim()) return tarefe;
        const query = tariffSearch.trim().toLowerCase();
        return tarefe.filter((item: any) => {
            const title = (item?.title || "").toLowerCase();
            const code = (item?.avarezCode || "").toString().toLowerCase();
            return title.includes(query) || code.includes(query);
        });
    }, [tarefe, tariffSearch]);

    const tarefeMap = useMemo(() => {
        const map = new Map<number, any>();
        tarefe.forEach(t => map.set(t.id, t));
        return map;
    }, [tarefe]);

    const handleChange = useCallback((id: any, value: any) => {
        setValues(prev => ({
            ...prev,
            [id]: value
        }));
    }, []);

    const handleAdd = useCallback((item: any) => {
        containerRef.current?.scrollTo({
            top: containerRef.current.scrollHeight + 10000,
            behavior: 'smooth',
        });
        const val = values[item.id];

        if (!val && item.amountType !== 1) {
            alert("لطفا مقدار پیش‌فرض را وارد کنید.");
            return;
        }

        if (isNaN(val) && item.amountType !== 1) {
            alert("لطفا فقط عدد وارد کنید.");
            return;
        }

        setSelectedItems((prev: any[]) => {
            const existingItemIndex = prev.findIndex(prevItem => prevItem.tariffId === item.id);

            if (existingItemIndex !== -1) {
                const updatedItems = [...prev];
                updatedItems[existingItemIndex] = {
                    vehicleTypeId: item.vehicleTypeId,
                    serviceTypeId: item.serviceTypeId,
                    tariffId: item.id,
                    description: item.description ?? '',
                    baseValue: item.amountType == 2 ? Number(val) : null,
                    Days: item.amountType == 3 ? Number(val) : null,
                    hours: item.amountType == 4 ? Number(val) : null,
                };
                return updatedItems;
            } else {
                return [
                    ...prev,
                    {
                        vehicleTypeId: item.vehicleTypeId,
                        serviceTypeId: item.serviceTypeId,
                        tariffId: item.id,
                        description: item.description,
                        baseValue: item.amountType == 2 ? Number(val) : null,
                        Days: item.amountType == 3 ? Number(val) : null,
                        hours: item.amountType == 4 ? Number(val) : null,
                    }
                ];
            }
        });
    }, [values]);

    useEffect(() => {
        billmodel
            .GetAll()
            .then((r) => {
                setBills(r.data.data);
            })
            .catch((e) => {
                ErrorToaast("خطا در برقراری ارتباط");
                navigate("/", { replace: true });
            });
    }, [counter, billmodel, navigate]);

    useEffect(() => {
        tModel
            .GetAll()
            .then((r) => {
                setTarefe(r.data.data);
            })
            .catch((e) => {
                ErrorToaast("خطا در برقراری ارتباط");
                navigate("/", { replace: true });
            });
    }, [tModel, navigate]);

    const handleEditClick = useCallback((data: any) => {
        billmodel
            .GetById(parseInt(data.id))
            .then((r) => {
                const res = r.data.data;
                setEditingId(res.id);
                formikRef.current.setFieldValue("year", res.year ? res.year.toString() : '1405');
                formikRef.current.setFieldValue("PlateNumber", res.plateNumber ?? '');
                formikRef.current.setFieldValue("Motaghazi", res.motaghazi ?? '');
                formikRef.current.setFieldValue("NationalCode", res.nationalCode ?? "");
                formikRef.current.setFieldValue("TaxiCode", res.taxiCode ?? "");
                setValues({});
                if (res.items && Array.isArray(res.items)) {
                    const items = res.items.map((i: any) => ({
                        vehicleTypeId: i.vehicleTypeName,
                        serviceTypeId: i.serviceTypeName,
                        tariffId: i.tariffId,
                        description: i.description ?? '',
                        baseValue: i.baseValue,
                        Days: i.days,
                        hours: i.hours,
                    }));
                    setSelectedItems(items);
                } else {
                    setSelectedItems([]);
                }
            })
            .catch((e) => {
                ErrorToaast("خطا در برقراری ارتباط");
            });
    }, [billmodel]);

    const handleRowClick = useCallback((data: any) => {
        handleEditClick(data);
    }, [handleEditClick]);

    const handleDeleteClick = useCallback((id: number) => {
        setDeleteOpen({ open: true, id });
    }, []);

    const handlePrintClick = useCallback((id: number) => {
        window.open(`https://hamlonaghlebaneh.ir/webroutes/billGhabz/${id}`, "_blank");
    }, []);

    const handleChangeStatusClick = useCallback((id: number, currentStatus: boolean) => {
        billmodel
            .ChangeStatus(id)
            .then(() => {
                SuccessToaast(
                    currentStatus
                        ? "وضعیت فیش به «پرداخت نشده» تغییر یافت"
                        : "وضعیت فیش به «پرداخت شده» تغییر یافت"
                );
                setBills((prev) =>
                    prev
                        ? prev.map((b) =>
                              b.id === id
                                  ? {
                                        ...b,
                                        isPaid: !currentStatus,
                                        paidDate: !currentStatus ? new Date().toISOString() : null,
                                    }
                                  : b
                          )
                        : prev
                );
            })
            .catch((e) => {
                ErrorToaast(e || "خطا در تغییر وضعیت پرداخت");
            });
    }, [billmodel]);

    const formik = useFormik({
        initialValues: {
            year: '1405',
            PlateNumber: "",
            Motaghazi: "",
            NationalCode: "",
            TaxiCode: ""
        },
        validationSchema,
        onSubmit: (data: any) => {
            if (editingId) {
                const payload = {
                    year: parseInt(data.year, 10) || 1405,
                    PlateNumber: data.PlateNumber || "",
                    Motaghazi: data.Motaghazi || "",
                    NationalCode: data.NationalCode || "",
                    TaxiCode: data.TaxiCode || "",
                    items: selectedItems,
                };
                billmodel
                    .EditData(editingId, payload)
                    .then(() => {
                        SuccessToaast("قبض با موفقیت ویرایش شد");
                        setCounter((c) => c + 1);
                        setEditingId(null);
                        formik.resetForm();
                        setValues({});
                        setSelectedItems([]);
                    })
                    .catch((e) => ErrorToaast(e || "خطا در ویرایش قبض"));
            } else {
                const payload = {
                    year: parseInt(data.year, 10) || 1405,
                    PlateNumber: data.PlateNumber || "",
                    Motaghazi: data.Motaghazi || "",
                    NationalCode: data.NationalCode || "",
                    TaxiCode: data.TaxiCode || "",
                };
                billmodel
                    .SubmitData(payload)
                    .then((r) => {
                        setCounter((c) => c + 1);
                        selectedItems.forEach((item) => {
                            billmodel.addItem(item, r.data.data.id);
                        });
                        SuccessToaast("قبض با موفقیت ثبت شد");
                        setValues({});
                        setSelectedItems([]);
                        formik.resetForm();
                    })
                    .catch((e) => ErrorToaast(e.message || e));
            }
        },
    });

    const formikRef = useRef(formik);
    formikRef.current = formik;

    const handleSearch = useCallback(() => {
        billmodel
            .GetSearch(
                formikRef.current.values.year,
                formikRef.current.values.PlateNumber,
                formikRef.current.values.Motaghazi,
                formikRef.current.values.NationalCode,
                formikRef.current.values.TaxiCode,
            ).then((r) => {
                setBills(r.data.data);
            })
            .catch((e) => ErrorToaast("مشکل در برقراری ارتباط"));
    }, [billmodel]);

    return (
        <DashboardLayout>
            <DashboardNavbar name={"صدور عوارض حمل و نقل"} />
            <MDBox>
                <Grid container columnGap={"5px"} wrap="nowrap">
                    <Grid container item xs={12} mb={4}>
                        <MDBox bgColor="#f8f8ff" borderRadius="5px" border={"0.8px #bababa solid"} component="form" role="form" onSubmit={formik.handleSubmit}>
                            <MDTypography fontSize="16px" textAlign="center" py={1}>
                                {editingId ? "ویرایش فیش" : "صدور فیش"}
                            </MDTypography>
                            <MDBox pt={"1px"} bgColor="black" mb={3} />
                            <Grid container alignItems={"center"} textAlign="center" rowGap={2} px={"12px"}>
                                <Grid item xs={2}>
                                    <MDTypography fontSize="14px"> سال </MDTypography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Autocomplete
                                        options={YEARS}
                                        value={formik.values.year || ""}
                                        disableClearable={true}
                                        getOptionLabel={(option) => option.toString()}
                                        onChange={(e, value) => formik.setFieldValue("year", value)}
                                        renderInput={(params) => (
                                            <MDInput
                                                {...params}
                                                size="small"
                                                name={'year'}
                                                error={formik.touched.year && Boolean(formik.errors.year)}
                                                helperText={formik.touched.year && formik.errors.year || ' '}
                                            />
                                        )}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <MDTypography fontSize="14px">
                                        متقاضی
                                    </MDTypography>
                                </Grid>
                                <Grid item xs={4}>
                                    <MDInput
                                        fullWidth
                                        size="small"
                                        name="Motaghazi"
                                        onChange={formik.handleChange}
                                        value={formik.values.Motaghazi}
                                        error={formik.touched.Motaghazi && Boolean(formik.errors.Motaghazi)}
                                        helperText={formik.touched.Motaghazi && formik.errors.Motaghazi || ' '}
                                        autoComplete="off"
                                    />
                                </Grid>

                                <Grid item xs={2}>
                                    <MDTypography fontSize="14px">
                                        کد ملی
                                    </MDTypography>
                                </Grid>
                                <Grid item xs={4}>
                                    <MDInput
                                        fullWidth
                                        size="small"
                                        onChange={formik.handleChange}
                                        name="NationalCode"
                                        value={formik.values.NationalCode}
                                        error={formik.touched.NationalCode && Boolean(formik.errors.NationalCode)}
                                        helperText={formik.touched.NationalCode && formik.errors.NationalCode || ' '}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <MDTypography fontSize="14px">
                                        پلاک
                                    </MDTypography>
                                </Grid>
                                <Grid item xs={4}>
                                    <MDInput
                                        fullWidth
                                        size="small"
                                        onChange={formik.handleChange}
                                        name="PlateNumber"
                                        value={formik.values.PlateNumber}
                                        helperText={' '}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <MDTypography fontSize="14px">
                                        کد تاکسی
                                    </MDTypography>
                                </Grid>
                                <Grid item xs={4}>
                                    <MDInput
                                        fullWidth
                                        size="small"
                                        onChange={formik.handleChange}
                                        name="TaxiCode"
                                        value={formik.values.TaxiCode}
                                        helperText={' '}
                                        autoComplete="off"
                                    />
                                </Grid>
                                <Grid item xs={6} />
                                <Grid container bgcolor={'#fafafa'} p={2} borderRadius={2} mt={3} alignItems={'center'}>
                                    <Grid container item xs={12} borderBottom={'1px solid #000'} pb={1} justifyContent="space-between" alignItems="center">
                                        <Grid item xs={12} sm={6}>
                                            <MDTypography fontSize="15px" sx={{ fontWeight: 'bold' }}>
                                                اضافه کردن تعرفه به قبض
                                            </MDTypography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <MDInput
                                                size="small"
                                                fullWidth
                                                placeholder="جستجوی تعرفه..."
                                                value={tariffSearch}
                                                onChange={(e: any) => setTariffSearch(e.target.value)}
                                                autoComplete="off"
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container maxHeight={'30vh'} sx={{ overflowY: 'auto' }} ref={containerRef}>
                                        <Grid container spacing={1} mt={2}>
                                            {filteredTarefe.length > 0 ? (
                                                filteredTarefe.map((item) => (
                                                    <TariffItemRow
                                                        key={item.id}
                                                        item={item}
                                                        value={values[item.id]}
                                                        onChange={handleChange}
                                                        onAdd={handleAdd}
                                                    />
                                                ))
                                            ) : (
                                                <Grid item xs={12} textAlign="center" py={2}>
                                                    <MDTypography fontSize="14px" color="text">
                                                        تعرفه‌ای یافت نشد
                                                    </MDTypography>
                                                </Grid>
                                            )}
                                        </Grid>

                                        {selectedItems.length > 0 && (
                                            <Grid container mt={4} p={2} bgcolor="#fff" borderRadius={2} border="1px solid #ddd">
                                                <Grid item xs={12} borderBottom="1px solid #ccc" pb={1} mb={2}>
                                                    <MDTypography fontSize="15px" sx={{ fontWeight: 'bold' }}>
                                                        تعرفه های اضافه‌شده
                                                    </MDTypography>
                                                </Grid>

                                                {selectedItems.map((si, index) => {
                                                    const tr = tarefeMap.get(si.tariffId);

                                                    return (
                                                        <Grid
                                                            container
                                                            key={si.tariffId || index}
                                                            alignItems="center"
                                                            sx={{
                                                                borderBottom: '1px solid #eee',
                                                                pb: 1,
                                                                mb: 1,
                                                            }}
                                                        >
                                                            <Grid item xs={6}>
                                                                <MDTypography fontSize="14px">
                                                                    {tr?.title} — مقدار:{" "}
                                                                    {si.baseValue || si.Days || si.hours || tr?.fixedAmount}
                                                                </MDTypography>
                                                            </Grid>

                                                            <Grid item xs={3}>
                                                                <MDButton
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="error"
                                                                    onClick={() =>
                                                                        setSelectedItems((prev) =>
                                                                            prev.filter((p) => p.tariffId !== si.tariffId)
                                                                        )
                                                                    }
                                                                >
                                                                    حذف
                                                                </MDButton>
                                                            </Grid>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                                <Grid container item p={2} columns={10} gap={2} wrap="nowrap">
                                    <Grid item xs={2}>
                                        <MDButton fullWidth width="100%" variant="contained" color="success" onClick={() => {
                                            setEditingId(null);
                                            formik.resetForm();
                                            setSelectedItems([]);
                                            setValues({});
                                            setTariffSearch("");
                                        }}>
                                            جدید
                                        </MDButton>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <MDButton fullWidth width="100%" variant="contained" color={editingId ? "info" : "warning"} type="submit">
                                            {editingId ? "ثبت تغییرات" : "ثبت"}
                                        </MDButton>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <MDButton fullWidth variant="contained" color="primary" onClick={handleSearch}>
                                            جستجو
                                        </MDButton>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <MDButton fullWidth variant="contained" color="info"
                                            onClick={() => window.open(`https://hamlonaghlebaneh.ir/webroutes/AllGhabz/list/designer?motaghazi=${formik.values.Motaghazi}&nationalCode=${formik.values.NationalCode}&year=${formik.values.year}&plateNumber=${formik.values.PlateNumber}&taxiCode=${formik.values.TaxiCode || ''}`, "_blank")}>
                                            چاپ همه
                                        </MDButton>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <MDBox minHeight="40vh" maxHeight="40vh" sx={{ overflow: "auto" }} bgColor="#adaabb" border="1px solid #535353">
                                <MDBox bgColor="#eeeeee" px={1}>
                                    <Grid container gap="0 10px" columns={16} textAlign="center" wrap="nowrap" width={"100%"} overflow={"auto"}>
                                        <Grid item xs={1} minWidth={"max-content"}>
                                            <MDTypography fontSize={"14px"}>کد قبض </MDTypography>
                                        </Grid>
                                        <Grid item xs={1} minWidth={"max-content"}>
                                            <MDTypography fontSize={"14px"}>سال قبض </MDTypography>
                                        </Grid>
                                        <Grid item xs={2}>
                                            <MDTypography fontSize={"14px"}>نام متقاضی </MDTypography>
                                        </Grid>
                                        <Grid item xs={2}>
                                            <MDTypography fontSize={"14px"}>کد ملی  </MDTypography>
                                        </Grid>
                                        <Grid item xs={2}>
                                            <MDTypography fontSize={"14px"}>شماره پلاک</MDTypography>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <MDTypography fontSize={"14px"}>کد تاکسی</MDTypography>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <MDTypography fontSize={"14px"}> مبلغ کل </MDTypography>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <MDTypography fontSize={"14px"}>وضعیت پرداخت </MDTypography>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <MDTypography fontSize={"14px"}>تاریخ صدور  </MDTypography>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <MDTypography fontSize={"14px"}>تاریخ پرداخت  </MDTypography>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <MDTypography fontSize={"14px"}>ویرایش</MDTypography>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <MDTypography fontSize={"14px"}>حذف</MDTypography>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <MDTypography fontSize={"14px"}>چاپ</MDTypography>
                                        </Grid>
                                    </Grid>
                                </MDBox>

                                {bills && bills.length > 0 &&
                                    bills.map((data: any, index: number) => (
                                        <BillRow
                                            key={data.id || index}
                                            data={data}
                                            index={index}
                                            onRowClick={handleRowClick}
                                            onEditClick={handleEditClick}
                                            onDeleteClick={handleDeleteClick}
                                            onPrintClick={handlePrintClick}
                                            onChangeStatusClick={handleChangeStatusClick}
                                        />
                                    ))}
                            </MDBox>
                        </MDBox>
                    </Grid>
                </Grid>
            </MDBox>
            <DeleteDialog
                open={deleteOpen.open}
                id={deleteOpen.id}
                setClose={() => setDeleteOpen({ id: 0, open: false })}
                onDone={() => {
                    setDeleteOpen({ open: false, id: 0 });
                    setCounter((c) => c + 1);
                }}
            />
        </DashboardLayout>
    );
}
